import type { ChangeEvent } from 'react';
import { EVENT_TYPES } from '@wineryxplorer/shared';
import type { EventType } from '@wineryxplorer/shared';

const formatEventType = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const ClearIcon = () => (
  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

interface EventFiltersProps {
  eventType: EventType | undefined;
  venueId: string;
  startDate: string;
  endDate: string;
  venues: { _id: string; name: string }[];
  onEventTypeChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onVenueChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  onStartDateChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onEndDateChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClearEventType: () => void;
  onClearVenue: () => void;
  onClearDateRange: () => void;
}

/**
 * Filter card for the Events page: type, venue, and date-range selects plus the
 * active-filter chips underneath.
 */
export const EventFilters = ({
  eventType,
  venueId,
  startDate,
  endDate,
  venues,
  onEventTypeChange,
  onVenueChange,
  onStartDateChange,
  onEndDateChange,
  onClearEventType,
  onClearVenue,
  onClearDateRange,
}: EventFiltersProps) => {
  const selectedVenue = venues.find((v) => v._id === venueId);

  return (
    <div className="bg-gray-50 rounded-lg shadow p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Event Type Filter */}
        <div>
          <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-1">
            Event Type
          </label>
          <select
            id="eventType"
            value={eventType || ''}
            onChange={onEventTypeChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500 sm:text-sm"
          >
            <option value="">All Types</option>
            {EVENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {formatEventType(type)}
              </option>
            ))}
          </select>
        </div>

        {/* Venue Filter */}
        <div>
          <label htmlFor="venue" className="block text-sm font-medium text-gray-700 mb-1">
            Venue
          </label>
          <select
            id="venue"
            value={venueId}
            onChange={onVenueChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500 sm:text-sm"
          >
            <option value="">All Venues</option>
            {venues.map((venue) => (
              <option key={venue._id} value={venue._id}>
                {venue.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filter */}
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={onStartDateChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={onEndDateChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Active Filters Display */}
      {(eventType || startDate || endDate || venueId) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {eventType && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
              Type: {formatEventType(eventType)}
              <button onClick={onClearEventType} className="ml-2 inline-flex items-center">
                <ClearIcon />
              </button>
            </span>
          )}
          {venueId && selectedVenue && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              Venue: {selectedVenue.name}
              <button onClick={onClearVenue} className="ml-2 inline-flex items-center">
                <ClearIcon />
              </button>
            </span>
          )}
          {(startDate || endDate) && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
              Date: {startDate || '...'} to {endDate || '...'}
              <button onClick={onClearDateRange} className="ml-2 inline-flex items-center">
                <ClearIcon />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
